<script setup lang="ts">
import { Ref, ref, watch } from 'vue'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from './library.store'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { useUserStore } from '@/authentication/user.store'
import { getCollectionUsers, grantContributors } from './api'
import ProgressSpinner from 'primevue/progressspinner'
import { ToastProvider, ToastRoot, ToastViewport } from 'reka-ui'
import { ContributorUserInfo } from './library_types'
import UserAutoComplete from './UserAutoComplete.vue'
import RemoveContributorModal from './RemoveContributorModal.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  },
})

const emit = defineEmits(['update:externalOpen'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const { currentFolder } = storeToRefs(libraryStore)
const showToast = ref(false)
const toastMessage = ref('')

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const updateError: Ref<boolean> = ref(false)
const updatingFolder: Ref<boolean> = ref(false)
const existingUsers: Ref<{user_uuid: string, role: string, user_name: string, user_email: string}[]> = ref([])
const loadingExistingUsers: Ref<boolean> = ref(false)
const errorExistingUsers: Ref<boolean> = ref(false)
const selectedUsers: Ref<ContributorUserInfo[]> = ref([])
const messageToNewContributors: Ref<string> = ref('')
const toastError: Ref<boolean> = ref(false)

// Watch for external open/close
watch(
  () => props.externalOpen,
  (newValue) => {
    visibleRef.value = newValue
    if (newValue) {
      fetchCollectionUsers()
    }
  },
  { immediate: true }
)

// Watch for internal changes and emit
watch(
  () => visibleRef.value,
  (newValue) => {
    if (newValue !== props.externalOpen) {
      emit('update:externalOpen', newValue)
    }
  }
)

const displayRole = function (role: string): string {
  return role[0].toUpperCase() + role.slice(1)
}

const closeModal = function (): void {
  visibleRef.value = false
  existingUsers.value = []
  selectedUsers.value = []
  messageToNewContributors.value = ''
}

const handleRemoveModalClosed = () => {
  fetchCollectionUsers()
  removeContributorModalOpen.value = false
  selectedUserUuid.value = ''
  selectedUserName.value = ''
}

const fetchCollectionUsers = async function() {
  if (!currentFolder.value?.uuid) return
  loadingExistingUsers.value = true
  errorExistingUsers.value = false
  try {
    const users = await getCollectionUsers(currentFolder.value.uuid)
    if (users && typeof users === 'object' && 'users' in users) {
      existingUsers.value = users.users as {user_uuid: string, role: string, user_name: string, user_email: string}[]
    } else {
      throw new Error('Invalid API response format')
    }
  } catch (e) {
    console.error('Failed to fetch collection users:', e)
    errorExistingUsers.value = true
  }
  loadingExistingUsers.value = false
}

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const addContributors = async function() {
  if (selectedUsers.value.length === 0) {
    return
  }

  updatingFolder.value = true
  try {
    const selectedUserUuids = selectedUsers.value.map(user => user.UserUUID)
    const message = messageToNewContributors.value
    await grantContributors(currentFolder.value.uuid, selectedUserUuids, message)
    updateError.value = false
    fetchCollectionUsers()

    analytics.sendEvent(EVENT_NAMES.LIBRARY.FOLDER_ACTION.CLICKED_SEND_SHARED_FOLDER_INVITATION, {
      customized_invite_message: !!message,
      invited_user_uuids: selectedUserUuids
    })

    closeModal()
    toastMessage.value = 'Share settings updated'
    showToast.value = true

    setTimeout(() => {
      showToast.value = false
    }, 3000)
  } catch (e) {
    updateError.value = true
    console.error('Failed to add contributors:', e)
  }
  updatingFolder.value = false
}

const removeContributorModalOpen = ref(false)
const selectedUserUuid = ref('')
const selectedUserName = ref('')

const removeContributors = async function(userUuid: string, name: string) {
  selectedUserUuid.value = userUuid
  selectedUserName.value = name
  removeContributorModalOpen.value = true
}

const handleUserSelected = (user: ContributorUserInfo) => {
  if (!selectedUsers.value.some(selected => selected.UserUUID === user.UserUUID)) {
    selectedUsers.value.push(user)
  }
}
const removeSelectedUser = (userUuid: string) => {
  selectedUsers.value = selectedUsers.value.filter(user => user.UserUUID !== userUuid)
  if (selectedUsers.value.length === 0) {
    messageToNewContributors.value = ''
    updateError.value = false
  }
}

const goBackToUserSelection = () => {
  selectedUsers.value = []
  messageToNewContributors.value = ''
  updateError.value = false
}
function sendContributorTooltipEvent() {
  analytics.sendEvent(EVENT_NAMES.LIBRARY.REVEALED_CONTRIBUTOR_TOOLTIP)
}
</script>

<template>
  <GbDialog
    dialogId="add-contributors-modal"
    v-model:visible="visibleRef"
    :footer-buttons="true"
    :show-header-content="true"
  >
    <template #headerContent>
      <div class="flex items-center justify-between px-2">
        <div class="flex items-center">
          <button
            type="button"
            @click="goBackToUserSelection"
            v-if="selectedUsers.length > 0"
            class="material-icons-round text-gray-700 hover:bg-gray-200 rounded-full p-1 mr-2 cursor-pointer"
            aria-label="Back to user selection"
          >
            arrow_back
          </button>
          <span class="text-xl leading-7" id="add-contributors-modal-header">Share </span>
          <span class="material-icons-round ml-2 mr-1 text-display-sm text-gray-700">folder</span>
          <span class="font-bold fs-exclude">{{currentFolder.name}}</span>
        </div>
      </div>
    </template>
    <template #bodyContent>
      <div class="flex flex-col gap-2 px-2">
        <div class="flex items-center justify-between">

          <h3 class="flex items-center font-bold">Add Contributors<span class="material-icons-round ml-1 text-xl cursor-pointer"
            v-tooltip.bottom="{
              value: 'Contributor must be a part of your organization and have an active Goalbook Toolkit license',
              class: 'tooltip-style tooltip-style-z'
            }">info_outline</span></h3>
          <span class="text-sm text-blue-400 underline decoration-dotted cursor-pointer"
            v-tooltip.bottom="{
              value: 'Contributors may add content to this folder but may not edit or remove content once it has been added.',
              class: 'tooltip-style tooltip-style-z'
              }"
            @mouseenter="sendContributorTooltipEvent"
          >
            What can a Contributor do?
          </span>
        </div>

        <div class="mb-8">
          <UserAutoComplete
            id="user-search"
            :domain-uuid="userStore.userDomainUuid"
            @user-selected="handleUserSelected"
            @user-removed="removeSelectedUser"
          />
        </div>

        <GbInlineMessage gbTheme="red" v-if="updateError">
          We couldn't update the folder contributors. Please try again later.
        </GbInlineMessage>
        <div v-if="selectedUsers.length > 0" class="mt-2">
          <textarea
            v-model="messageToNewContributors"
            class="w-full placeholder-gray-700 py-2 px-3 border border-gray-700 rounded-sm fs-exclude"
            placeholder="Message (optional)"
            rows="5"
            maxlength="999"
          />
          <GbInlineMessage gbTheme="red" v-if="messageToNewContributors.length > 999">
            Message must be 999 characters or less.
          </GbInlineMessage>
        </div>
        <div v-else>
          <h3 class="font-bold mb-2">Folder members</h3>
          <div class="flex flex-col">
            <div v-if="errorExistingUsers">
              <GbInlineMessage gbTheme="red"><span class="flex items-center"><span class="material-icons-round text-red-300">warning_amber</span><span class="ml-2">We're not able to retrieve folder members. Please try again later.</span></span></GbInlineMessage>
            </div>
            <div v-else-if="loadingExistingUsers" class="p-4 flex flex-col items-center justify-center">
              <ProgressSpinner class="h-8 w-8 stroke-primary-400" strokeWidth="8"/>
              <p class="text-center grow p-2">Loading...</p>
            </div>
            <template v-else>
              <div v-for="user in existingUsers" :key="user.user_uuid" class="flex items-center justify-between border-l-2 border-gray-400 border-solid p-2 folder-member">
                <span class="font-medium fs-exclude">{{user.user_name}}<br/><span class="text-sm font-regular fs-exclude">{{user.user_email}}</span></span>
                <div class="flex items-center"><span class="text-md font-medium">{{ displayRole(user.role) }} </span>
                  <button
                    class="remove-collaborator material-icons-round text-lg ml-2 cursor-pointer hover:bg-gray-300 rounded-full p-1 fs-exclude"
                    v-if="user.role !== 'owner'"
                    @click="removeContributors(user.user_uuid, user.user_name)"
                    :aria-label="`Remove ${user.user_name} as contributor`"
                    type="button">
                    close
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footerButtonRight>
      <GbButton
        type="button"
        :gbTheme="selectedUsers.length > 0 ? 'blueTertiary' : 'bluePrimary'"
        :label="selectedUsers.length > 0 ? 'Cancel' : 'Done'"
        @click="closeModal"
      />
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        label="Send"
        @click="addContributors"
        v-if="selectedUsers.length > 0"
      />
    </template>
  </GbDialog>
  <ToastProvider>
    <ToastRoot
      :open="showToast"
      @update:open="showToast = $event"
      class="rounded-md shadow-sm py-2 px-4 grid grid-cols-[auto_max-content] items-center data-[state=open]:animate-slideIn data-[state=closed]:animate-hide fixed top-16 left-1/2 transform -translate-x-1/2 z-50"
      :class="toastError ? 'bg-red-100 text-default border-red-300 border-1' : 'bg-black text-white'"
    >
      <div class="flex items-center">
        <span class="material-icons-outlined text-display-sm" v-if="!toastError">group</span>
        <span class="material-icons-round text-display-sm text-red-300" v-else>warning_amber</span>
        <span class="ml-2">{{ toastMessage }}</span>
      </div>
    </ToastRoot>
    <ToastViewport :label="`Notification - add contributors`" class="fixed top-0 left-0 right-0 flex justify-center p-[25px] gap-[10px] w-full max-w-[100vw] m-0 list-none z-100 outline-none" />
  </ToastProvider>
  <RemoveContributorModal
    v-model:externalOpen="removeContributorModalOpen"
    :userUuid="selectedUserUuid"
    :userName="selectedUserName"
    @modal-closed="handleRemoveModalClosed"
  />
</template>
